import type { PlanFeature, Plan, PlanTransformOptions } from '../../../types/plans';
import type { PaymentPlan, PlanType } from '../../../types/payment';
import type { UserProfile } from '../../../types/auth';
import { UNIFIED_PLANS, FREE_PLAN, ALL_PLANS } from '../../../data/plans';
import { PLAN_LIMITS, type PlanId } from '../../../config/planConfig';
import { env } from '../../core/env';
export function getPaymentPlanById(planId: PlanType): PaymentPlan | undefined {
  const plan = UNIFIED_PLANS.find(p => p.id === planId);
  if (!plan) return undefined;

  return transformToPaymentPlan(plan);
}

export function transformToPlan(unifiedPlan: (typeof UNIFIED_PLANS)[0] | typeof FREE_PLAN): Plan {
  return {
    id: unifiedPlan.id,
    name: unifiedPlan.name,
    description: unifiedPlan.description,
    monthlyPrice: unifiedPlan.monthlyPrice,
    yearlyPrice: unifiedPlan.yearlyPrice,
    features: unifiedPlan.features,
    buttonText: unifiedPlan.buttonText,
    isRecommended: unifiedPlan.isRecommended,
    isPopular: unifiedPlan.isPopular,
    isFree: unifiedPlan.isFree,
    isComingSoon: unifiedPlan.isComingSoon,
    upgradeEntry: unifiedPlan.upgradeEntry,
    // Price-description update (YEP-588)
    dailyPrice: unifiedPlan.dailyPrice,
    taglineNote: unifiedPlan.taglineNote,
    billingDescription: unifiedPlan.billingDescription,
    isContactSales: unifiedPlan.isContactSales,
  };
}

export function transformToPaymentPlan(unifiedPlan: (typeof UNIFIED_PLANS)[0]): PaymentPlan {
  return {
    id: unifiedPlan.id as PlanType,
    name: unifiedPlan.name,
    description: unifiedPlan.description,
    monthlyPrice: unifiedPlan.monthlyPrice,
    yearlyPrice: unifiedPlan.yearlyPrice,
    features: convertFeaturesToStrings(unifiedPlan.features),
    credits: unifiedPlan.credits || 0,
    isRecommended: unifiedPlan.isRecommended || false,
  };
}

export function convertFeaturesToStrings(features: PlanFeature[], includeOnly = true): string[] {
  return features.filter(feature => !includeOnly || feature.included).map(feature => feature.text);
}
export function getAllPlansForSelection(options: PlanTransformOptions = {}): Plan[] {
  const { includeFree = true } = options;
  const plansToTransform = includeFree ? ALL_PLANS : UNIFIED_PLANS;

  return plansToTransform.map(plan => transformToPlan(plan));
}
export function getFreePlan(): Plan {
  return transformToPlan(FREE_PLAN);
}

export function isValidPaymentPlan(planId: string): planId is PlanType {
  return UNIFIED_PLANS.some(plan => plan.id === planId);
}
export function formatPlanIdForUrl(planName: string): string {
  return planName.toLowerCase().replace(/[\s-]/g, '');
}

export function parsePlanIdFromUrl(urlPlanId: string): string | undefined {
  // Handle special mappings
  const urlToIdMap: Record<string, string> = {
    enterprise: 'enterprise',
    startup: 'startup',
    starter: 'starter',
    basic: 'basic',
    free: 'free',
    business: 'business',
    premium: 'premium',
    // Digital Staff credit plans (Core / Growth).
    ai_starter: 'ai_starter',
    ai_growth: 'ai_growth',
  };

  return urlToIdMap[urlPlanId.toLowerCase()];
}

export interface FilteredPlans {
  paidPlans: Plan[];
  freePlan: Plan | null;
}

/**
 * Get plan's product training limit from config
 * Returns -1 for unlimited, or the limit number
 */
function getPlanProductLimit(planId: string): number {
  const limits = PLAN_LIMITS[planId as PlanId];
  return limits?.productsTraining ?? -1;
}

/**
 * Check if a plan can support the given product count
 * -1 means unlimited (always supported)
 */
function canPlanSupportProductCount(planId: string, productCount: number): boolean {
  const limit = getPlanProductLimit(planId);
  // -1 means unlimited
  if (limit === -1) return true;
  return productCount <= limit;
}

export function getFilteredPlansBasedOnProductCount(productCount: number | null): FilteredPlans {
  const freePlan = getFreePlan();

  // If productCount is null (loading/error), show all plans as default
  if (productCount === null) {
    return {
      paidPlans: getAllPlansForSelection({ includeFree: false }),
      freePlan: null,
    };
  }

  // Get all paid plans
  const allPaidPlans = getAllPlansForSelection({ includeFree: false });

  // Filter plans based on their product training limits from PLAN_LIMITS
  const filteredPaidPlans = allPaidPlans.filter(plan =>
    canPlanSupportProductCount(plan.id, productCount)
  );

  // Check if free plan can support the product count
  const canShowFreePlan = canPlanSupportProductCount('free', productCount);

  return {
    paidPlans: filteredPaidPlans,
    freePlan: canShowFreePlan ? freePlan : null,
  };
}

/**
 * Price Plan v2: narrow a plan list to what a cohort should see.
 * - LEGACY: legacy tiers (starter/basic/startup) + Business as the one-way
 *   "upgrade to AI Team" entry.
 * - NEW (and unknown/loading): Business + Premium only.
 */
export function filterPlansByCohort(plans: Plan[], cohort: 'LEGACY' | 'NEW' | null): Plan[] {
  if (cohort === 'LEGACY') {
    return plans
      .filter(p => ['starter', 'basic', 'startup', 'business'].includes(p.id))
      .map(p => (p.id === 'business' ? { ...p, upgradeEntry: true, isRecommended: false } : p));
  }
  // NEW or unknown → new pricing
  return plans.filter(p => ['business', 'premium'].includes(p.id));
}

/**
 * Price Plan v2 — agent unlock by plan tier (used by the sidebar workspaces and
 * the AI Team roster). Maps the real backend plan to the set of usable agents:
 *   Starter (any/none)      → Maya
 *   Growth  (business)      → Maya + Anna
 *   Pro     (premium)       → Maya + Anna + Alex
 *   Scale   (—, not live)   → all
 * Every plan (incl. free / unpaid / legacy) gets Maya; higher agents require the
 * paid NEW-cohort tiers. Trials map to their base plan via
 * getCurrentPlanFromPaymentStatus.
 */
/**
 * Price Plan v2 — the user's plan tier on the new ladder
 * (1=Starter, 2=Growth, 3=Pro, 4=Scale). Drives the AI Team page gating + the
 * "Your Plan" card. business→Growth, premium→Pro; everything else
 * (free / unpaid / legacy / no plan) falls back to Starter. Scale has no
 * backend plan yet.
 */
export function getPlanTierFromPaymentStatus(
  paymentStatus?: string | null,
  cohort?: 'LEGACY' | 'NEW' | null
): number {
  const plan = paymentStatus ? getCurrentPlanFromPaymentStatus(paymentStatus) : null;
  if (plan === 'ai_scale') return 4;
  if (plan === 'premium' || plan === 'ai_pro') return 3;
  if (plan === 'business' || plan === 'ai_growth') return 2;
  // Legacy-cohort users are grandfathered onto Growth — gifted, not purchased.
  if (cohort === 'LEGACY') return 2;
  return 1; // ai_starter / free / legacy / no plan → Starter
}

export function getUnlockedAgentSlugs(
  paymentStatus?: string | null,
  cohort?: 'LEGACY' | 'NEW' | null
): Set<string> {
  const plan = paymentStatus ? getCurrentPlanFromPaymentStatus(paymentStatus) : null;
  const unlocked = new Set<string>(['maya']);
  const growthOrUp = plan === 'business' || plan === 'premium'
    || plan === 'ai_growth' || plan === 'ai_pro' || plan === 'ai_scale'
    // Legacy cohort is gifted Growth (Maya + Anna).
    || cohort === 'LEGACY';
  const proOrUp = plan === 'premium' || plan === 'ai_pro' || plan === 'ai_scale';
  if (growthOrUp) unlocked.add('anna');
  if (proOrUp) unlocked.add('alex');
  return unlocked;
}

/**
 * Oscar, unlocked for everyone on DEV only.
 *
 * <p>Pro and Scale are `comingSoon` in chatbot-api's CreditPlanCatalog — nobody can buy them — so
 * on dev there is no plan that unlocks Oscar and no way to exercise his pages, which is exactly
 * where they need exercising (Monica, 2026-08-12). The same switch the sidebar already uses for the
 * Operation nav group (`env.appEnv === 'development'`), so dev has ONE Oscar switch rather than two
 * that can disagree.
 *
 * <p>⚠️ This is a TEST affordance, not an entitlement: the backend still decides what is
 * provisioned, and on test/prod this is false — Oscar stays locked there until a plan that includes
 * him is purchasable.
 *
 * <p>⚠️ 代价：`npm run dev` 下这个开关把 Oscar 对所有账号打开，套餐门控被整个盖掉，本地
 * 「谁能看到 Oscar」永远是全开。要在本地验证按套餐分档的真实行为，改用
 *
 * <pre>npm run dev:plan</pre>
 *
 * 它只是 `VITE_APP_ENV=staging vite`：appEnv 不再是 development，这个开关随之失效，而 API
 * 仍然打 dev 后端（.env.development 的其余变量原样生效），dev 的测试账号照常能登。
 * Vite 的 loadEnv 里 process.env 的 VITE_* 覆盖 .env 文件；确认是否生效看
 * `curl localhost:5173/src/lib/core/env.ts` 头部注入的 import.meta.env。只覆盖 appEnv 是安全的：
 * firebase / stripe 的 key 都来自各自独立的 VITE_* 变量，GTM 的本地拦截走 hostname 判断。
 * 同一个坑在 vitest 里也有（.env.test 同样是 development），那边靠 planUtils.test.ts 顶部的
 * vi.mock 把 appEnv 钉成 production。
 */
const OPERATION_OPEN_ON_DEV = env.appEnv === 'development';

/**
 * The agent TYPES (`agent_tasks.agent_type`) a plan unlocks — the same ladder as
 * {@link getUnlockedAgentSlugs}, keyed the way the server talks about agents.
 *
 * Mirrors chatbot-api's `CreditPlanCatalog.allowedAgents`: Starter/Free = Maya alone,
 * Growth adds Anna, Pro adds Alex and Oscar, Scale adds agents that do not exist yet.
 * Kept as a second copy on purpose — the slug version gates AI Team pages, this one gates
 * anything driven by task data (the Store Manager ledger), and neither can be derived from
 * the other: `operation` has no slug in the roster at all.
 *
 * `customer_support` and `customer` both appear in the wild for the merchant-side agent, so both
 * are emitted; a caller matching either spelling gets the same answer.
 */
export function getUnlockedAgentTypes(
  paymentStatus?: string | null,
  cohort?: 'LEGACY' | 'NEW' | null
): Set<string> {
  const tier = getPlanTierFromPaymentStatus(paymentStatus, cohort);
  const types = new Set<string>(['marketing']);
  if (tier >= 2) {
    /*
     * Customer support is TWO agents, and they unlock together (fuxu, 2026-08-17).
     *
     * - to C — the storefront agent the shopper talks to: `concierge`, which is exactly what
     *   `DigitalStaffAgentTypes` already means by it ("Storefront customer service").
     * - to B — Anna, the merchant's own console: `customer` (`customer_support` is the plan
     *   catalog's spelling of the same thing).
     *
     * One tier buys both, so one `if` covers them; a caller matching any of the three spellings
     * gets the same answer without normalising first — normalising at each call site is how one
     * of them gets missed.
     */
    types.add('customer_support');
    types.add('customer');
    types.add('concierge');
  }
  if (tier >= 3) {
    types.add('sales');
    types.add('operation');
  }
  /*
   * 回馈老用户：Oscar 临时对 Core($199) / Growth($299) 以及 LEGACY 老客户开放
   * （产品，2026-09-22；LEGACY 于 2026-09-23 追加）。
   *
   * 付费两档按 plan code 判，不能写成 `tier >= 1` —— tier 1 同时是 free / unpaid / 无订阅的
   * 兜底档，那样等于把 Oscar 送给没付钱的人。
   *
   * LEGACY 判 cohort 而不是判 `plan_code == NULL`：NULL 里混着三种人（老轨老客户 / 从没订过 /
   * 已取消），只有 cohort 能把老轨那批择出来。他们买的是改版前的旧套餐，新套餐对他们根本不可见，
   * 所以永远不会有 ai_* 的 plan code —— 跟「没付钱」是两回事。这批人本来就被赠予了 Growth 档
   * （见 getPlanTierFromPaymentStatus 的 `cohort === 'LEGACY'` → 2，Maya + Anna 也是送的），
   * Oscar 跟着一起送，口径一致。
   *
   * ponytail: 写死的临时开关，撤回就是删掉下面这个 if；等 Oscar 正式进 CreditPlanCatalog 的
   * ai_starter/ai_growth.allowedAgents 之后改回按 tier 判。
   * ⚠️ prod 还需要后端把 digital-staff.operation-enabled 翻开，否则卡片能点、接口 400。
   * ⚠️ LEGACY 年底要迁到新价目表（见 EoyBanner），届时这份赠予是收回还是跟着迁，得再定。
   */
  const planCode = paymentStatus ? getCurrentPlanFromPaymentStatus(paymentStatus) : null;
  if (planCode === 'ai_starter' || planCode === 'ai_growth' || cohort === 'LEGACY') {
    types.add('operation');
  }
  if (OPERATION_OPEN_ON_DEV) {
    types.add('operation');
  }
  return types;
}

/**
 * Every agent type the plan ladder has an opinion about — all spellings of each.
 *
 * <p>Used to tell "this agent is locked" from "we have never heard of this agent". A payload can
 * name an agent a newer backend has and this build does not; dropping it because it is absent from
 * the ladder would hide a real agent on the word of a stale enum, so only KNOWN types are gateable.
 */
const GATEABLE_AGENT_TYPES = new Set<string>([
  'marketing',
  'customer_support',
  'customer',
  'concierge',
  'sales',
  'operation',
]);

/**
 * True when the plan ladder knows this agent AND this merchant has not unlocked it.
 *
 * @param unlockedAgentTypes from {@link getUnlockedAgentTypes}
 */
export function isAgentLockedByPlan(agentType: string, unlockedAgentTypes: Set<string>): boolean {
  return GATEABLE_AGENT_TYPES.has(agentType) && !unlockedAgentTypes.has(agentType);
}

/**
 * The plan code to gate on for a user. Credit-plan (ai_*) users keep
 * chatbot_payment_status = "unpaid", so their real plan lives in `creditPlanCode`
 * (from /user); fall back to the legacy paymentStatus otherwise. Pass the result
 * to getPlanTierFromPaymentStatus / getUnlockedAgentSlugs / getCurrentPlanFromPaymentStatus.
 */
export function effectivePlanStatus(user?: {
  creditPlanCode?: string | null;
  payment?: { paymentStatus?: string | null };
} | null): string | null | undefined {
  return user?.creditPlanCode ?? user?.payment?.paymentStatus;
}

export const getCurrentPlanFromPaymentStatus = (paymentStatus: string): string | null => {
  switch (paymentStatus) {
    case 'basic':
    case 'basicTrial':
      return 'basic';
    case 'starter':
    case 'starterTrial':
      return 'starter';
    case 'startup':
    case 'startupTrial':
      return 'startup';
    case 'pro':
    case 'proTrial':
      return 'enterprise';
    case 'business':
    case 'businessTrial':
      return 'business';
    case 'premium':
    case 'premiumTrial':
      return 'premium';
    // Digital Staff credit plans — return the code itself so billing-UI tier
    // matching (realPlanId === currentPlan) and tier/agent gating work. `free`
    // is intentionally not a "current paid plan" → null.
    case 'ai_starter':
      return 'ai_starter';
    case 'ai_growth':
      return 'ai_growth';
    case 'ai_pro':
      return 'ai_pro';
    case 'ai_scale':
      return 'ai_scale';
    case 'unpaid':
    case 'expired':
    case 'free':
    default:
      return null;
  }
};

/**
 * Frontend plan id -> backend plan code (the inverse of
 * getCurrentPlanFromPaymentStatus). The only historical shift is the frontend
 * "enterprise" card, which is the backend "pro" plan. Used by the Stripe
 * payment flow so chatbot-api can run the Price Plan v2 cohort validation
 * (T3.1 / D3) on /payment/create.
 */
export const getBackendPlanCode = (planId: string): string => {
  return planId === 'enterprise' ? 'pro' : planId;
};

export interface SubscriptionPagePlans {
  plans: Plan[];
}

/**
 * Get plans for the subscription page with proper filtering logic:
 * - Only show plans that can support user's product count, sorted by price
 */
export function getPlansForSubscriptionPage(
  _user: UserProfile | null,
  _isYearlyTab: boolean,
  productCount: number | null
): SubscriptionPagePlans {
  // Get all paid plans
  const allPaidPlans = getAllPlansForSelection({ includeFree: false });

  let sortedPaidPlans: Plan[];

  if (productCount !== null) {
    // Only show plans that can support the product count
    sortedPaidPlans = allPaidPlans
      .filter(plan => canPlanSupportProductCount(plan.id, productCount))
      .sort((a, b) => a.monthlyPrice - b.monthlyPrice);
  } else {
    // If no product count, sort all by price
    sortedPaidPlans = allPaidPlans.sort((a, b) => a.monthlyPrice - b.monthlyPrice);
  }

  return { plans: sortedPaidPlans };
}
